import { useState, useRef, useEffect, useCallback } from 'react';

interface ImageViewerProps {
  imageUrl: string;
  alt: string;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  imageUrl,
  alt,
  onClose,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [positionStart, setPositionStart] = useState({ x: 0, y: 0 });
  const [isMobile] = useState(window.innerWidth <= 768);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // ズーム・パンのリセット
  const resetTransform = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // 画像が変更されたときにリセット
  useEffect(() => {
    resetTransform();
  }, [imageUrl, resetTransform]);

  // マウスホイール（PC）でのズーム
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();

    const delta = e.deltaY;
    const zoomFactor = delta > 0 ? 0.9 : 1.1;

    setScale(prevScale => {
      const newScale = Math.max(0.5, Math.min(5, prevScale * zoomFactor));
      return newScale;
    });
  }, []);

  // マウス/タッチ操作の開始
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setPositionStart(position);

      if (containerRef.current) {
        containerRef.current.setPointerCapture(e.pointerId);
      }
    },
    [position]
  );

  // マウス/タッチ操作の移動
  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;

      e.preventDefault();
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      setPosition({
        x: positionStart.x + deltaX,
        y: positionStart.y + deltaY,
      });
    },
    [isDragging, dragStart, positionStart]
  );

  // マウス/タッチ操作の終了
  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    setIsDragging(false);

    if (containerRef.current) {
      containerRef.current.releasePointerCapture(e.pointerId);
    }
  }, []);

  // ピンチズーム（タッチデバイス）
  const [lastTouchDistance, setLastTouchDistance] = useState(0);

  const getTouchDistance = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;

    const touch1 = touches[0];
    const touch2 = touches[1];

    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) +
        Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      setLastTouchDistance(getTouchDistance(e.touches));
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && lastTouchDistance > 0) {
        e.preventDefault();

        const currentDistance = getTouchDistance(e.touches);
        const distanceRatio = currentDistance / lastTouchDistance;

        setScale(prevScale => {
          const newScale = Math.max(
            0.5,
            Math.min(5, prevScale * distanceRatio)
          );
          return newScale;
        });

        setLastTouchDistance(currentDistance);
      }
    },
    [lastTouchDistance]
  );

  const handleTouchEnd = useCallback(() => {
    setLastTouchDistance(0);
  }, []);

  // イベントリスナーの設定
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // キーボードショートカット
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case '=':
        case '+':
          e.preventDefault();
          setScale(prev => Math.min(5, prev * 1.2));
          break;
        case '-':
          e.preventDefault();
          setScale(prev => Math.max(0.5, prev / 1.2));
          break;
        case '0':
          e.preventDefault();
          resetTransform();
          break;
        case 'ArrowLeft':
          if (hasPrevious && onPrevious) {
            e.preventDefault();
            onPrevious();
          }
          break;
        case 'ArrowRight':
          if (hasNext && onNext) {
            e.preventDefault();
            onNext();
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext, hasPrevious, hasNext, resetTransform]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : scale > 1 ? 'grab' : 'zoom-in',
        userSelect: 'none',
        touchAction: 'pan-x pan-y pinch-zoom',
      }}
      onClick={e => {
        // 背景クリックで閉じる（画像クリック時は除外）
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <img
        ref={imageRef}
        src={imageUrl}
        alt={alt}
        style={{
          maxWidth: scale === 1 ? '95%' : 'none',
          maxHeight: scale === 1 ? '95%' : 'none',
          width: scale === 1 ? 'auto' : `${scale * 100}%`,
          height: scale === 1 ? 'auto' : 'auto',
          objectFit: 'contain',
          transform: `translate(${position.x}px, ${position.y}px)`,
          transition: isDragging ? 'none' : 'transform 0.1s ease-out',
          pointerEvents: 'none',
        }}
        draggable={false}
      />

      {/* ツールバー */}
      <div
        style={{
          position: 'absolute',
          top: isMobile ? '10px' : '20px',
          right: isMobile ? '10px' : '20px',
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
        }}
      >
        {/* ズーム表示 */}
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          {Math.round(scale * 100)}%
        </div>

        {/* リセットボタン */}
        {(scale !== 1 || position.x !== 0 || position.y !== 0) && (
          <button
            onClick={resetTransform}
            style={{
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: isMobile ? '36px' : '40px',
              height: isMobile ? '36px' : '40px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="リセット (0キー)"
          >
            ↺
          </button>
        )}

        {/* 閉じるボタン */}
        <button
          onClick={onClose}
          style={{
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: isMobile ? '36px' : '40px',
            height: isMobile ? '36px' : '40px',
            cursor: 'pointer',
            fontSize: isMobile ? '16px' : '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="閉じる (Escキー)"
        >
          ✕
        </button>
      </div>

      {/* ナビゲーションボタン */}
      {hasPrevious && onPrevious && (
        <button
          onClick={e => {
            e.stopPropagation();
            onPrevious();
          }}
          style={{
            position: 'absolute',
            left: isMobile ? '10px' : '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: isMobile ? '44px' : '50px',
            height: isMobile ? '44px' : '50px',
            cursor: 'pointer',
            fontSize: isMobile ? '20px' : '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="前の画像 (←キー)"
        >
          ‹
        </button>
      )}

      {hasNext && onNext && (
        <button
          onClick={e => {
            e.stopPropagation();
            onNext();
          }}
          style={{
            position: 'absolute',
            right: isMobile ? '10px' : '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: isMobile ? '44px' : '50px',
            height: isMobile ? '44px' : '50px',
            cursor: 'pointer',
            fontSize: isMobile ? '20px' : '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="次の画像 (→キー)"
        >
          ›
        </button>
      )}
    </div>
  );
};

export default ImageViewer;
